import React, { useContext, useEffect, useRef } from 'react'
import { StateContext } from '../../services/StateService'
import { Translations } from '../../services/TranslationService'
import ScreenTip from '@3mcom/mds-library/dist/node/ScreenTip'

/**
 * WTB (Where To Buy) ScreenTip — primary CTA for the isParentWithHiddenSKUs flow.
 *
 * Flow overview:
 *  1. CTASection only mounts this component when isParentWithHiddenSKUs is true
 *     (isParent && !displayTable && options.length > 0). That condition is enforced
 *     upstream by PdpCallService._validateSelectorsAvailability, which clears options
 *     when childHasCcSubId, !discontinued, or !pdpSelectorOverride are not satisfied.
 *     No need to re-check those conditions here.
 *
 *  2. Visibility is driven by selectedOptions.wtbMmmIds (set by WhereToBuyController):
 *     - null  → ScreenTip visible, WTB widget hidden  (initial state / after clear)
 *     - set   → ScreenTip hidden, WTB widget loading  (user has made selections)
 *
 *  3. When user clicks the ScreenTip:
 *     - highlightSelectors is toggled → Selectors.jsx applies a red ring to unselected dropdowns
 *     - ScreenTip closes via screenTipRef.toggle()
 *
 *  4. The useEffect closes an already-open ScreenTip when the user starts making
 *     selections (totalSelected changes while highlightSelectors is still active),
 *     then resets highlightSelectors to avoid stale red rings.
 */
const WTBScreenTip = () => {
    const { moreOptions, selectedOptions, highlightSelectors, dispatch } =
        useContext(StateContext)
    const translations = useContext(Translations)
    const { whereToBuy, selectOptionsTooltip } = translations

    const screenTipRef = useRef(null)

    /** Close ScreenTip and reset highlight state when user starts selecting (see flow step 4 above) */
    useEffect(() => {
        if (
            selectedOptions?.totalSelected !== moreOptions?.totalSelected &&
            highlightSelectors
        ) {
            screenTipRef?.current?.toggle()
            dispatch({
                type: 'pdp_update_UI_options',
                payload: {
                    highlightSelectors: false
                }
            })
        }
    }, [
        selectedOptions?.totalSelected,
        moreOptions?.totalSelected,
        highlightSelectors,
        dispatch
    ])

    const onClickScreenTip = () => {
        dispatch({
            type: 'pdp_update_UI_options',
            payload: { highlightSelectors: !highlightSelectors }
        })
        screenTipRef?.current?.toggle()
    }

    return (
        <>
            {/* wtbMmmIds missing or empty = no widget active yet; show ScreenTip so user knows WTB is available */}
            {!(selectedOptions?.wtbMmmIds?.length > 0) && (
                <ScreenTip
                    ref={screenTipRef}
                    buttonName={whereToBuy}
                    content={selectOptionsTooltip}
                    disableAutoToggle={true}
                    onClick={onClickScreenTip}
                    tealiumTracking={true}
                    btnTealiumObj={{
                        event_name: `PDP Buying Zone Purchase Options Button Click: ${whereToBuy}`,
                        event_type: 'Engagement Event',
                        experience_type: 'SNAPS2',
                        wtb_click: 1
                    }}
                />
            )}
        </>
    )
}

export default WTBScreenTip
